import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography
} from '@mui/material';
import { UserPermissionsConfig } from './UserPermissionsConfig';
import { useUserPermissions } from './hooks/useUserPermissions';
import { useNotification } from '../../../../components/index';

export const EditUserDialog = ({
    open,
    onClose,
    selectedUserTaskAccess,
    originalFiles,
    getDefaultPermissionsForRole,
    onUserTaskAccessUpdated,
    handleUpdateTaskAccesses,
    currentUserAccess,
    onSendNotifications
}) => {
    const { canConfigureFilePermissions, getAccessibleFiles } = useUserPermissions(currentUserAccess);
    const accessibleFiles = getAccessibleFiles(originalFiles);
    const [userPermissions, setUserPermissions] = useState({
        role: 'Viewer',
        advancedMode: false,
        customCanManageTeam: false,
        filePermissions: {}
    });
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const { showError } = useNotification();

    // Полностью сбрасываем состояние при закрытии диалога
    useEffect(() => {
        if (!open) {
            setIsInitialized(false);
            setUserPermissions({
                role: 'Viewer',
                advancedMode: false,
                customCanManageTeam: false,
                filePermissions: {}
            });
        }
    }, [open]);

    // Инициализируем права пользователя при открытии диалога
    useEffect(() => {
        if (open && selectedUserTaskAccess && !isInitialized) {
            initializeUserPermissions();
        }
    }, [open, selectedUserTaskAccess, isInitialized]);

    const initializeUserPermissions = () => {
        if (!selectedUserTaskAccess) return;

        const currentPermissions = {
            role: selectedUserTaskAccess.role || 'Viewer',
            advancedMode: false,
            customCanManageTeam: selectedUserTaskAccess.canManageTeam || false,
            filePermissions: {}
        };

        // Загружаем существующие права без изменений для всех файлов
        originalFiles.forEach(file => {
            const fileAccess = selectedUserTaskAccess.fileAccess.find(x => x.baseFileId === file.baseFileId);
            const fileKey = `${file.fileName}_v${file.fileVersion}`;

            if (fileAccess) {
                // Сохраняем исходные права без изменений
                currentPermissions.filePermissions[fileKey] = {
                    canRead: fileAccess.canRead ?? false,
                    canEdit: fileAccess.canEdit ?? false,
                    canDeleteOwnVersions: fileAccess.canDeleteOwnVersions ?? false,
                    canDeleteAllVersions: fileAccess.canDeleteAllVersions ?? false
                };
            } else {
                // Если прав нет, устанавливаем по умолчанию false
                currentPermissions.filePermissions[fileKey] = {
                    canRead: false,
                    canEdit: false,
                    canDeleteOwnVersions: false,
                    canDeleteAllVersions: false
                };
            }
        });
        setUserPermissions(currentPermissions);
        setIsInitialized(true);
    };

    const handleSave = async () => {
        if (!selectedUserTaskAccess) return;

        setLoading(true);
        try {
            const userPerms = userPermissions;

            // Подготавливаем права доступа к файлам
            const fileAccesses = originalFiles.map(file => {
                const fileAccess = selectedUserTaskAccess.fileAccess.find(x => x.baseFileId === file.baseFileId);
                const fileKey = `${file.fileName}_v${file.fileVersion}`;

                let permissions = userPerms.filePermissions[fileKey];

                return {
                    id: fileAccess.id,
                    baseFileId: fileAccess.baseFileId,
                    canRead: Boolean(permissions?.canRead),
                    canEdit: Boolean(permissions?.canEdit),
                    canDeleteOwnVersions: Boolean(permissions?.canDeleteOwnVersions),
                    canDeleteAllVersions: Boolean(permissions?.canDeleteAllVersions),
                    addedAt: fileAccess.addedAt,
                    addedByUserId: fileAccess.addedByUserId,
                    removedAt: fileAccess.removedAt,
                    removedByUserId: fileAccess.removedByUserId,
                    removalReason: fileAccess.removalReason
                };
            });

            const updateData = {
                id: selectedUserTaskAccess.id,
                taskId: selectedUserTaskAccess.taskId,
                user: selectedUserTaskAccess.user,
                role: userPerms.role,
                canManageTeam: Boolean(userPerms.customCanManageTeam),
                fileAccess: fileAccesses,
                addedAt: selectedUserTaskAccess.addedAt,
                addedByUserGuid: selectedUserTaskAccess.addedByUserGuid,
                removedAt: selectedUserTaskAccess.removedAt,
                removedByUserGuid: selectedUserTaskAccess.removedByUserGuid,
                removalReason: selectedUserTaskAccess.removalReason
            };

            const success = await handleUpdateTaskAccesses(updateData);

            if (success) {
                onUserTaskAccessUpdated();
                onClose();
            }
        } catch (error) {
            console.error('Ошибка обновления прав пользователя:', error);
            showError('Ошибка обновления прав пользователя');
        } finally {
            setLoading(false);
        }

        if (selectedUserTaskAccess.user?.login && onSendNotifications) {
            await onSendNotifications([selectedUserTaskAccess.user?.login], 'permissions_updated');
        }
    };

    // Обработчик изменения роли
    const handleRoleChange = (newRole) => {
        const defaultPermissions = getDefaultPermissionsForRole(newRole);

        setUserPermissions(prev => {
            const updatedFilePermissions = { ...prev.filePermissions };

            // Обновляем права только для доступных файлов
            originalFiles.forEach(file => {
                const fileKey = `${file.fileName}_v${file.fileVersion}`;
                const canConfigure = canConfigureFilePermissions(file);

                if (canConfigure) {
                    updatedFilePermissions[fileKey] = { ...defaultPermissions };
                }
                // Для недоступных файлов оставляем текущие права без изменений
            });

            return {
                ...prev,
                role: newRole,
                customCanManageTeam: newRole === 'Admin' || newRole === 'Creator',
                filePermissions: updatedFilePermissions
            };
        });
    };

    // Обработчик изменения расширенного режима
    const handleAdvancedModeChange = (advancedMode) => {
        setUserPermissions(prev => ({
            ...prev,
            advancedMode
        }));
    };

    // Обработчик изменения управления командой
    const handleCustomCanManageTeamChange = (customCanManageTeam) => {
        setUserPermissions(prev => ({
            ...prev,
            customCanManageTeam
        }));
    };

    // Обработчик изменения прав файла
    const handleFilePermissionChange = (fileKey, permissionType, value) => {
        const file = originalFiles.find(f =>
            `${f.fileName}_v${f.fileVersion}` === fileKey
        );

        if (file && !canConfigureFilePermissions(file)) {
            return;
        }

        setUserPermissions(prev => ({
            ...prev,
            filePermissions: {
                ...prev.filePermissions,
                [fileKey]: {
                    ...prev.filePermissions[fileKey],
                    [permissionType]: value
                }
            }
        }));
    };


    if (!selectedUserTaskAccess) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Редактирование прав пользователя
            </DialogTitle>

            <DialogContent>
                <Box mt={2}>
                    {isInitialized ? (
                        <UserPermissionsConfig
                            key={`${selectedUserTaskAccess.user.guid}_${isInitialized}`}
                            user={selectedUserTaskAccess.user}
                            selectedRole={userPermissions.role}
                            onRoleChange={handleRoleChange}
                            advancedMode={userPermissions.advancedMode}
                            onAdvancedModeChange={handleAdvancedModeChange}
                            customCanManageTeam={userPermissions.customCanManageTeam}
                            onCustomCanManageTeamChange={handleCustomCanManageTeamChange}
                            filePermissions={userPermissions.filePermissions}
                            onFilePermissionChange={handleFilePermissionChange}
                            allFiles={accessibleFiles}
                            getDefaultPermissionsForRole={getDefaultPermissionsForRole}
                            currentUserAccess={currentUserAccess}
                        />
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                            <CircularProgress size={30} />
                            <Typography variant="body2" style={{ marginLeft: 10 }}>
                                Загрузка прав...
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                </Button>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    className="dt1520-button active-dt1520-button view-button default-button"
                >
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};